import { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { fetchStats, MODE_LABELS } from '../../../lib/prompt-admin-api';

export function StatsTab() {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            setLoading(true);
            const data = await fetchStats();
            setStats(data.stats);
        } catch (error) {
            toast.error('Nie udało się załadować statystyk');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    const periods = [
        { key: 'today', label: 'Dzisiaj' },
        { key: 'week', label: 'Ten tydzień' },
        { key: 'month', label: 'Ten miesiąc' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Statystyki generacji</h2>
                <Button variant="outline" size="sm" onClick={loadStats}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Odśwież
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {periods.map(period => (
                    <div key={period.key} className="bg-card border border-border rounded-xl p-6">
                        <h3 className="text-lg font-medium mb-4">{period.label}</h3>
                        <div className="text-4xl font-bold text-primary mb-4">
                            {stats?.[period.key]?.total || 0}
                        </div>
                        <div className="space-y-2">
                            {Object.entries(stats?.[period.key]?.byMode || {}).map(([mode, data]: [string, any]) => (
                                <div key={mode} className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{MODE_LABELS[mode] || mode}</span>
                                    <span>
                                        <span className="text-green-500">{data.success}</span>
                                        {data.error > 0 && <span className="text-red-500 ml-2">/{data.error} err</span>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Insights */}
            {stats?.insights && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Success Rate */}
                    <div className="bg-card border border-border rounded-xl p-6">
                        <h3 className="text-lg font-medium mb-4">Success Rate</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Dzisiaj</span>
                                <span className={`text-2xl font-bold ${stats.insights.successRateToday >= 90 ? 'text-green-500' : stats.insights.successRateToday >= 70 ? 'text-yellow-500' : 'text-red-500'}`}>
                                    {stats.insights.successRateToday}%
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Tydzień</span>
                                <span className="text-lg font-semibold">{stats.insights.successRateWeek}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Miesiąc</span>
                                <span className="text-lg font-semibold">{stats.insights.successRateMonth}%</span>
                            </div>
                        </div>
                    </div>

                    {/* Top Modes */}
                    <div className="bg-card border border-border rounded-xl p-6">
                        <h3 className="text-lg font-medium mb-4">Najpopularniejsze tryby</h3>
                        <div className="space-y-3">
                            {stats.insights.topModes.map(([mode, data]: [string, any], idx: number) => (
                                <div key={mode} className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl font-bold text-primary">{idx + 1}</span>
                                        <span className="text-sm">{MODE_LABELS[mode] || mode}</span>
                                    </div>
                                    <span className="text-sm font-semibold">{data.total}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Errors Summary */}
                    <div className="bg-card border border-border rounded-xl p-6">
                        <h3 className="text-lg font-medium mb-4">Błędy (miesiąc)</h3>
                        <div className="space-y-2">
                            {Object.entries(stats.month.byMode).map(([mode, data]: [string, any]) => (
                                data.error > 0 && (
                                    <div key={mode} className="flex justify-between items-center text-sm">
                                        <span className="text-muted-foreground">{MODE_LABELS[mode] || mode}</span>
                                        <span className="text-red-500 font-semibold">{data.error}</span>
                                    </div>
                                )
                            ))}
                            {Object.values(stats.month.byMode).every((data: any) => data.error === 0) && (
                                <p className="text-sm text-muted-foreground text-center py-4">Brak błędów 🎉</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
